import { useEffect, useMemo, useState, useRef } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, CalendarDays, Loader2, Mic } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { localeTagFor, type Lang } from "@/i18n/locale";
import {
  type useAgendaTasks,
  type AgendaTask,
  type Priority,
} from "@/hooks/useAgendaTasks";
import QuickTaskModal, {
  type QuickTaskPrefill,
} from "@/components/agenda/QuickTaskModal";
import {
  useTaskVoiceCapture,
  type ExtractedAgendaEvent,
} from "@/hooks/useTaskVoiceCapture";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { usePendingTasks } from "@/lib/taskChanges";
// isSyncedTask ERA chamada na linha ~313 sem NUNCA ter sido importada — desde
// 03/07 (d32628d0), 27 dias. ReferenceError fatal a cada render de tarefa
// (VYV-FRONTEND-48). Os outros 4 consumidores importam daqui; só este não.
// O `tsc` via isso desde o primeiro dia (TS2304); ninguém rodava `tsc` no CI,
// porque `npm run build` é o Vite e o Vite transpila sem checar tipo. Agora
// roda: `npm run frontend:types` (scripts/frontend-type-check.mjs).
import { isSyncedTask } from "@/hooks/useTasks";
import ImportedTaskActionModal from "@/components/mirror/ImportedTaskActionModal";
import { snapshotForTask } from "@/lib/taskMutations";
import { taskDueWeekday } from "@/lib/taskDates";
import type { TaskSnapshot } from "@/lib/taskExecutor";

/**
 * Frente C Fase 1 — Tasks card mounted inside /agenda between the month
 * calendar block and the "HOJE" event list. Two sections (HOJE / ESTA
 * SEMANA), inline edit, confirmed toggle, promotion to calendar event
 * via the parent's CreateEventModal mount (we don't render the modal
 * here — we just emit the intent up via onPromoteToEvent).
 *
 * Scheduled-task badge: when a task has external_id set (i.e., previously
 * promoted to an agenda_event), we fetch the linked event's start_at
 * once on mount and surface a small "Agendada para HH:MM" label so the
 * user knows the row is double-booked. The badge stays a read-only hint;
 * the agenda card itself owns event editing.
 */

interface AgendaTasksCardProps {
  userId: string;
  tasksState: ReturnType<typeof useAgendaTasks>;
  onCreated: () => void;
  onPromoteToEvent: (task: AgendaTask) => void;
  // Voice capture event-fallback (Bug A 13/05/26): when task-extract
  // returns kind_mismatch:event_detected, the hook re-routes the same
  // transcript through agenda-extract and we bubble the events up so
  // the parent (Agenda.tsx) opens CreateEventModal pre-filled.
  onVoiceEvent?: (events: ExtractedAgendaEvent[], transcript: string) => void;
}

type EventTimeMap = Record<string, string>; // event_id → ISO start_at

function priorityDot(priority: Priority): string {
  if (priority === "Critical") return "hsl(0 80% 60%)";
  if (priority === "Important") return "hsl(187 72% 60%)";
  return "hsl(210 14% 60%)";
}

function dueDateChip(dueIso: string | null, lang: Lang): string {
  if (!dueIso) return "";
  const locale = localeTagFor(lang);
  return taskDueWeekday(dueIso, locale);
}

function scheduledTime(iso: string, lang: Lang): string {
  const locale = localeTagFor(lang);
  return new Date(iso).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
}

export default function AgendaTasksCard({ userId, tasksState, onCreated, onPromoteToEvent, onVoiceEvent }: AgendaTasksCardProps) {
  const { t, tf, lang } = useLanguage();
  const {
    tasksToday,
    tasksWeek,
    loading,
    loadError,
    refetch,
    addTask,
    toggleDone,
    updateTask,
    deleteTask,
  } = tasksState;

  const pendingTasks = usePendingTasks();
  const editSaving = useRef(false);
  const [editError, setEditError] = useState(false);
  const editSnapshot = useRef<TaskSnapshot | null>(null);
  const [editStale, setEditStale] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickPrefill, setQuickPrefill] = useState<QuickTaskPrefill | undefined>(undefined);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [linkedTimes, setLinkedTimes] = useState<EventTimeMap>({});
  const [providerAction, setProviderAction] = useState<{ task: AgendaTask; action: "update" | "complete" } | null>(null);
  const ownerEpoch = useRef(0);
  useEffect(() => { ownerEpoch.current++; setQuickOpen(false); setProviderAction(null); setEditingId(null); setEditValue(""); editSnapshot.current = null; const generation = ownerEpoch; return () => { generation.current++; }; }, [userId]);

  // Voice capture (Bug A 13/05/26). Toggle pattern: tap mic → recording;
  // tap again → stop → transcribe → extract → route result.
  const voice = useTaskVoiceCapture(lang, userId);

  useEffect(() => {
    if (!voice.result) return;
    if (voice.result.kind === "task") {
      setQuickPrefill({
        title: voice.result.data.title,
        due_date: voice.result.data.due_date,
        priority: voice.result.data.priority,
      });
      setQuickOpen(true);
    } else if (voice.result.kind === "event") {
      if (voice.result.events.length === 0) {
        toast.error(t("agenda.tasks.voiceFailed"));
      } else if (onVoiceEvent) {
        onVoiceEvent(voice.result.events, voice.result.transcript);
      }
    }
    voice.reset();
  }, [voice.result, onVoiceEvent, t, voice]);

  useEffect(() => {
    if (voice.error) {
      toast.error(t("agenda.tasks.voiceFailed"));
      voice.reset();
    }
  }, [voice.error, t, voice]);

  // Drop the prefill once the modal closes so a subsequent manual [+] tap
  // gets a clean form.
  useEffect(() => {
    if (!quickOpen && quickPrefill) {
      const id = window.setTimeout(() => setQuickPrefill(undefined), 300);
      return () => window.clearTimeout(id);
    }
  }, [quickOpen, quickPrefill]);

  const handleMicTap = () => {
    if (voice.status === "recording") {
      void voice.stop();
    } else if (voice.status === "idle" || voice.status === "ready" || voice.status === "error") {
      void voice.start();
    }
    // transcribing/extracting → no-op, button shows spinner
  };

  // One-shot fetch of linked-event start_at values for the scheduled badge.
  // Re-runs when the set of external_ids changes (after a promotion).
  const linkedIds = useMemo(() => {
    const ids: string[] = [];
    for (const t of [...tasksToday, ...tasksWeek]) {
      if (t.external_id && !isSyncedTask(t)) ids.push(t.external_id);
    }
    return Array.from(new Set(ids));
  }, [tasksToday, tasksWeek]);

  useEffect(() => {
    let cancelled = false;
    if (linkedIds.length === 0) {
      setLinkedTimes({});
      return;
    }
    (async () => {
      const { data, error } = await supabase
        .from("agenda_events")
        .select("id, start_at")
        .in("id", linkedIds);
      if (cancelled) return;
      if (error) {
        console.warn("[AgendaTasksCard] linked-events fetch failed:", error);
        return;
      }
      const map: EventTimeMap = {};
      for (const row of (data ?? []) as Array<{ id: string; start_at: string }>) {
        map[row.id] = row.start_at;
      }
      setLinkedTimes(map);
    })();
    return () => { cancelled = true; };
  }, [linkedIds]);

  const handleQuickSubmit = async (input: {
    id: string;
    title: string;
    due_date: string | null;
    priority: Priority;
    addToCalendar: boolean;
  }) => {
    const generation = ownerEpoch.current;
    const created = await addTask({
      id: input.id,
      title: input.title,
      due_date: input.due_date,
      priority: input.priority,
    });
    if (!created) {
      throw new Error("Task creation was not confirmed");
    }
    if (generation !== ownerEpoch.current) return;
    onCreated();
    toast.success(t(input.due_date ? "common.saved" : "ux.agendaTasks.savedUndated"));
    if (input.addToCalendar) {
      // Defer one tick so the modal close animation completes before the
      // CreateEventModal opens — otherwise vaul portal + framer fight.
      window.setTimeout(() => { if (generation === ownerEpoch.current) onPromoteToEvent(created); }, 240);
    }
  };

  const startEdit = (task: AgendaTask) => {
    if (isSyncedTask(task)) { setProviderAction({ task, action: "update" }); return; }
    setEditError(false);
    setEditStale(false); editSnapshot.current = snapshotForTask(task);
    setEditingId(task.id);
    setEditValue(task.title);
  };

  const commitEdit = async (task: AgendaTask) => {
    const next = editValue.trim();
    if (!next || editSaving.current || editStale) return;
    if (next === editSnapshot.current?.task.title) { setEditingId(null); return; }
    editSaving.current = true;
    setEditError(false);
    try {
      if (!editSnapshot.current) throw new Error("invalid_snapshot");
      await updateTask(task.id, { title: next }, editSnapshot.current);
      setEditingId(null);
      toast.success(t("common.saved"));
    } catch (cause) {
      setEditError(true);
      if (cause instanceof Error && /changed|stale|snapshot/.test(cause.message)) setEditStale(true);
    } finally { editSaving.current = false; }
  };

  const cancelEdit = () => {
    if (editSaving.current) return;
    setEditingId(null);
    setEditValue("");
    setEditError(false);
  };

  const handleToggle = async (task: AgendaTask) => {
    if (isSyncedTask(task)) { setProviderAction({ task, action: "complete" }); return; }
    try { const expected = snapshotForTask(task); if (!expected) throw new Error("invalid_snapshot"); await toggleDone(task.id, true, expected); }
    catch { toast.error(t("ux.tasks.mutationError")); }
  };

  const handleDelete = async (task: AgendaTask) => {
    const expected = snapshotForTask(task);
    if (!window.confirm(t("agenda.tasks.deleteConfirm"))) return;
    try {
      if (!expected) throw new Error("invalid_snapshot"); await deleteTask(task.id, expected);
    } catch (e) {
      const blocked = e instanceof Error && e.message === "SYNCED_TASK_DELETE_BLOCKED";
      toast.error(t(blocked ? "tasks.deleteSyncedBlocked" : "tasks.deleteError"));
    }
  };

  const renderTask = (task: AgendaTask, showDayChip: boolean) => {
    const isEditing = editingId === task.id;
    const pending = pendingTasks.has(task.id);
    const linkedIso = task.external_id ? linkedTimes[task.external_id] : undefined;

    return (
      <div
        key={task.id}
        aria-busy={pending}
        className="flex items-center gap-3 rounded-xl px-3 py-2.5"
        style={{
          background: "rgba(255,255,255,0.04)",
          border: "0.5px solid rgba(255,255,255,0.08)",
        }}
      >
        {/* Checkbox */}
        <button
          type="button"
          onClick={() => void handleToggle(task)}
          disabled={pending || isEditing}
          className="shrink-0 w-5 h-5 rounded-md flex items-center justify-center transition-all active:scale-90"
          style={{
            border: `1.5px solid ${priorityDot(task.priority)}`,
            background: "transparent",
          }}
          aria-label={t("common.done")}
        >
          {pending && <Loader2 size={12} className="animate-spin" aria-label={t("ux.tasks.saving")} />}
        </button>

        {/* Title — inline edit toggle */}
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <form onSubmit={(event) => { event.preventDefault(); void commitEdit(task); }} className="space-y-2">
              <input autoFocus value={editValue} disabled={pending || editStale}
                onChange={(event) => setEditValue(event.target.value)}
                onKeyDown={(event) => { if (event.key === "Escape") { event.preventDefault(); cancelEdit(); } }}
                className="w-full bg-transparent outline-none text-sm text-white border-b border-cyan-400/40 pb-1 disabled:opacity-50"
                aria-label={t("agenda.tasks.editPlaceholder")} />
              {editError && <p role="alert" className="text-sm text-amber-200">{t(editStale ? "taskAction.changed" : "ux.tasks.saveError")}</p>}
              {editStale && <button type="button" className="min-h-11 px-3" onClick={() => { cancelEdit(); void refetch(); }}>{t("taskAction.refreshReview")}</button>}
              <div className="flex flex-wrap gap-2">
                <button type="submit" disabled={pending || editStale || !editValue.trim()} className="min-h-11 px-3 rounded-lg border border-cyan-400/30 text-sm text-cyan-200 disabled:opacity-40">{t("common.save")}</button>
                <button type="button" disabled={pending} onClick={cancelEdit} className="min-h-11 px-3 text-sm text-white/70 disabled:opacity-40">{t("common.cancel")}</button>
              </div>
            </form>
          ) : (
            <button
              type="button"
              onClick={() => startEdit(task)}
              disabled={pending || editingId !== null}
              className="block w-full text-left truncate text-[13px] font-display text-white"
              title={t("agenda.tasks.editPlaceholder")}
            >
              {task.title}
            </button>
          )}
          {linkedIso && (
            <p className="text-[10px] mt-0.5 truncate" style={{ color: "rgba(34,211,238,0.85)" }}>
              📅 {tf("agenda.tasks.scheduled", { time: scheduledTime(linkedIso, lang) })}
            </p>
          )}
        </div>

        {/* Day chip (week section only) */}
        {showDayChip && task.due_date && (
          <span
            className="font-display tabular-nums uppercase shrink-0"
            style={{
              fontSize: 10,
              letterSpacing: "0.10em",
              color: "rgba(255,255,255,0.55)",
            }}
          >
            {dueDateChip(task.due_date, lang)}
          </span>
        )}

        {/* Promote button — hidden when already linked */}
        {!task.external_id && (
          <button
            type="button"
            onClick={() => onPromoteToEvent(task)}
            disabled={pending || isEditing}
            className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-all active:scale-90"
            style={{
              background: "rgba(34,211,238,0.08)",
              border: "0.5px solid rgba(34,211,238,0.22)",
            }}
            aria-label={t("agenda.tasks.promote")}
            title={t("agenda.tasks.promote")}
          >
            <CalendarDays size={13} className="text-cyan-300/85" />
          </button>
        )}

        {/* Delete — hidden for provider-synced rows (rule 30/06: the next
            google-tasks-pull would resurrect them; delete in Google instead) */}
        {!isSyncedTask(task) && (
          <button
            type="button"
            onClick={() => void handleDelete(task)}
            disabled={pending || isEditing}
            className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-all active:scale-90 opacity-60 hover:opacity-100"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "0.5px solid rgba(255,255,255,0.08)",
            }}
            aria-label={t("agenda.tasks.delete")}
            title={t("agenda.tasks.delete")}
          >
            <Trash2 size={12} className="text-white/55" />
          </button>
        )}
      </div>
    );
  };

  const totalCount = tasksToday.length + tasksWeek.length;
  const isEmpty = !loading && !loadError && totalCount === 0;

  return (
    <>
      {providerAction && <ImportedTaskActionModal open task={providerAction.task} action={providerAction.action}
        onClose={() => setProviderAction(null)} onCreated={() => { void refetch(); }} />}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.10, duration: 0.5 }}
        className="rounded-2xl p-4 mb-5 relative z-10 ring-1 ring-white/5"
        style={{ background: "linear-gradient(135deg, #0a0e1a 0%, #0f1419 50%, #0a0e1a 100%)" }}
      >
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <p
            className="font-display uppercase"
            style={{
              fontSize: 10,
              letterSpacing: "0.25em",
              fontWeight: 500,
              color: "rgba(255,255,255,0.55)",
            }}
          >
            {t("agenda.tasks.title")}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleMicTap}
              disabled={voice.status === "starting" || voice.status === "transcribing" || voice.status === "extracting" || !voice.supported}
              className={
                "min-h-11 px-3 gap-2 text-sm text-cyan-300 rounded-lg flex items-center justify-center focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40" +
                (voice.status === "recording"
                  ? " ring-2 ring-cyan-400/60 animate-pulse"
                  : "")
              }
              style={{
                background: "rgba(34,211,238,0.10)",
                border: "0.5px solid rgba(34,211,238,0.30)",
              }}
              aria-label={t(voice.status === "recording" ? "ux.agendaTasks.stop" : "agenda.tasks.voiceAdd")}
              title={
                voice.status === "recording"
                  ? t("agenda.tasks.voiceListening")
                  : voice.status === "starting" || voice.status === "transcribing" || voice.status === "extracting"
                    ? t("agenda.tasks.voiceProcessing")
                    : t("agenda.tasks.voiceAdd")
              }
            >
              {voice.status === "starting" || voice.status === "transcribing" || voice.status === "extracting" ? (
                <Loader2 size={14} className="text-cyan-300 animate-spin" />
              ) : (
                <Mic size={14} className="text-cyan-300" />
              )}
              {t(voice.status === "recording" ? "ux.agendaTasks.stop" : "ux.notes.dictate")}
            </button>
            <button
              type="button"
              onClick={() => setQuickOpen(true)}
              className="min-h-11 px-3 gap-2 text-sm text-cyan-300 rounded-lg flex items-center justify-center focus-visible:ring-2 focus-visible:ring-primary"
              style={{
                background: "rgba(34,211,238,0.10)",
                border: "0.5px solid rgba(34,211,238,0.30)",
              }}
              aria-label={t("agenda.tasks.add")}
              title={t("agenda.tasks.add")}
            >
              <Plus size={14} className="text-cyan-300" />{t("agenda.tasks.add")}
            </button>
          </div>
        </div>

        <p className="text-sm text-white/65 mb-3">{t("ux.agendaTasks.scope")}</p>
        {loadError && (
          <div role="alert" className="rounded-xl border border-amber-400/30 p-3 mb-3 text-sm text-white">
            <p>{t("ux.agendaTasks.loadError")}</p>
            <button type="button" onClick={() => void refetch()} className="min-h-11 text-cyan-300 underline focus-visible:ring-2 focus-visible:ring-primary">
              {t("ux.notes.retry")}
            </button>
          </div>
        )}
        {loading && totalCount === 0 ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-4 h-4 animate-spin text-white/40" />
          </div>
        ) : isEmpty ? (
          <div className="flex flex-col items-center text-center py-4 gap-3">
            <p className="text-sm text-white/65">{t("ux.agendaTasks.emptyWeek")}</p>
            <button
              type="button"
              onClick={() => setQuickOpen(true)}
              className="min-h-11 flex items-center gap-1.5 px-3 rounded-lg text-sm font-display focus-visible:ring-2 focus-visible:ring-primary"
              style={{
                background: "rgba(34,211,238,0.10)",
                border: "0.5px solid rgba(34,211,238,0.30)",
                color: "rgba(34,211,238,0.95)",
              }}
            >
              <Plus size={11} />
              {t("agenda.tasks.addFirst")}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {tasksToday.length > 0 && (
              <div className="space-y-1.5">
                <p
                  className="font-display uppercase"
                  style={{
                    fontSize: 10,
                    letterSpacing: "0.18em",
                    color: "rgba(34,211,238,0.65)",
                  }}
                >
                  {t("agenda.tasks.today")} <span className="tabular-nums text-white/45">({tasksToday.length})</span>
                </p>
                <div className="space-y-1.5">
                  {tasksToday.map((task) => renderTask(task, false))}
                </div>
              </div>
            )}
            {tasksWeek.length > 0 && (
              <div className="space-y-1.5">
                <p
                  className="font-display uppercase"
                  style={{
                    fontSize: 10,
                    letterSpacing: "0.18em",
                    color: "rgba(255,255,255,0.45)",
                  }}
                >
                  {t("agenda.tasks.week")} <span className="tabular-nums text-white/45">({tasksWeek.length})</span>
                </p>
                <div className="space-y-1.5">
                  {tasksWeek.map((task) => renderTask(task, true))}
                </div>
              </div>
            )}
          </div>
        )}
      </motion.div>

      <QuickTaskModal
        open={quickOpen}
        onOpenChange={setQuickOpen}
        onSubmit={handleQuickSubmit}
        prefill={quickPrefill}
      />
    </>
  );
}
